import React, { useState, useEffect } from 'react';
import Banner from '../components/common/Banner';
import { apiService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { SecureContent } from '../utils/sanitize';

const PlacementsPage = () => {
  const [placementsData, setPlacementsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPlacementsData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getPlacements();
        if (response && response.success && response.data) {
          setPlacementsData(response.data);
        } else {
          throw new Error('Invalid API response structure');
        }
      } catch (error) {
        setError('Failed to load placements data');
        console.error('Error fetching placements data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlacementsData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-crimson mb-4"></i>
          <p className="text-gray-600">Loading Placements Page...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full fade-in">
      <Banner 
        title={placementsData?.bannerTitle || "Training & Placements"}
        subtitle={placementsData?.bannerSubtitle || "Career Development | Industry Partnerships | Professional Growth"}
        imageSrc={placementsData?.bannerImage ? (placementsData.bannerImage.startsWith('http') ? placementsData.bannerImage : getImageUrl(placementsData.bannerImage)) : "https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"}
      />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 md:p-10 space-y-8 sm:space-y-12">
        
        {/* Placement Coordinator Message */}
        <section>
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
            <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 items-center">
              <div className="bg-slate-50 p-6 rounded-lg text-center">
                {placementsData?.coordinatorImage ? (
                  <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
                    <img
                      src={placementsData.coordinatorImage}
                      alt={placementsData.coordinatorName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-24 h-24 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-user-tie text-white text-3xl"></i>
                  </div>
                )}
                <h4 className="font-bold text-slate">{placementsData?.coordinatorName || 'Dr. [Name]'}</h4>
                <p className="text-sm text-gray-500">{placementsData?.coordinatorTitle || 'Placement Coordinator'}</p>
              </div>
              
              <div className="md:col-span-2 space-y-4">
                <h2 className="text-xl sm:text-2xl font-sans font-bold text-slate">Message from Placement Coordinator</h2>
                <div className="text-gray-600 leading-relaxed">
                  <SecureContent 
                    content={placementsData?.coordinatorMessage || 'Welcome to the Training and Placement Cell of FiT Engineering College. Our dedicated team works tirelessly to bridge the gap between academic excellence and industry requirements. We are committed to providing our students with comprehensive career guidance, skill development programs, and placement opportunities with leading organizations across various sectors.'}
                    className="rich-text-content"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Career Guidance & Capacity Building */}
        <section>
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-3 sm:mb-4">
              {placementsData?.careerGuidanceTitle || 'Career Guidance & Capacity Building'}
            </h2>
            <p className="text-gray-500">
              {placementsData?.careerGuidanceDescription || 'Comprehensive programs designed to enhance employability and career readiness.'}
            </p>
          </div>

          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {placementsData?.careerPrograms?.map((program, index) => (
              <div key={index} className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
                <div className={`w-12 h-12 ${program.color} rounded-lg flex items-center justify-center mb-4`}>
                  <i className={`${program.icon} text-white`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-slate mb-2 sm:mb-3">{program.title}</h3>
                <ul className="text-gray-600 text-sm sm:text-base space-y-1">
                  {program.items?.map((item, itemIndex) => (
                    <li key={itemIndex}>• {item}</li>
                  ))}
                </ul>
              </div>
            )) || (
              // Fallback default programs if no data
              <>
                <div className="bg-white p-4 sm:p-6 rounded-xl border border-border_gray">
                  <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                    <i className="fas fa-brain text-white"></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-slate mb-2 sm:mb-3">Soft Skills Development</h3>
                  <ul className="text-gray-600 text-sm sm:text-base space-y-1">
                    <li>• Communication skills workshops</li>
                    <li>• Leadership training programs</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Our Recruiters */}
        <section>
          <div className="mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-3 sm:mb-4">
              {placementsData?.recruitersTitle || 'Our Recruiters'}
            </h2>
            <p className="text-gray-500">
              {placementsData?.recruitersDescription || 'Leading organizations that regularly recruit from our campus.'}
            </p>
          </div>

          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {(placementsData?.recruitersList || [
                { name: 'Microsoft', logo: 'fab fa-microsoft' },
                { name: 'Google', logo: 'fab fa-google' },
                { name: 'Amazon', logo: 'fab fa-amazon' },
                { name: 'Apple', logo: 'fab fa-apple' }
              ]).map((recruiter, index) => (
                <div key={index} className="text-center group">
                  <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-crimson transition duration-300 overflow-hidden">
                    {recruiter.logoImage ? (
                      <img 
                        src={recruiter.logoImage.startsWith('http') ? recruiter.logoImage : getImageUrl(recruiter.logoImage)}
                        alt={`${recruiter.name} Logo`}
                        className="w-full h-full object-contain p-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <div 
                      className="text-center text-gray-400" 
                      style={{ display: recruiter.logoImage ? 'none' : 'block' }}
                    >
                      <i className="fas fa-building text-3xl mb-1"></i>
                      <p className="text-xs">{recruiter.logoImage ? 'Failed to Load' : 'No Logo'}</p>
                    </div>
                  </div>
                  <h4 className="font-semibold text-slate text-sm">{recruiter.name}</h4>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
              <h4 className="font-semibold text-blue-800 mb-2">Placement Statistics</h4>
              <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-4 text-blue-700">
                <div>
                  <div className="text-2xl font-bold">{placementsData?.placementRate || '90%+'}</div>
                  <div className="text-sm">Placement Rate</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{placementsData?.partnerCompanies || '150+'}</div>
                  <div className="text-sm">Partner Companies</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{placementsData?.highestPackage || '₹8L+'}</div>
                  <div className="text-sm">Highest Package</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section>
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl border border-border_gray">
            <h2 className="text-xl sm:text-2xl font-sans font-bold text-slate mb-4 sm:mb-6">Get in Touch</h2>
            <div>
              <h4 className="font-semibold text-slate mb-4">For Students</h4>
              <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm sm:text-base">
                <span><i className="fas fa-envelope text-crimson mr-2"></i> {placementsData?.studentEmail || 'fit.vikaskumar2025@gmail.com'}</span>
                <span><i className="fas fa-phone text-crimson mr-2"></i> {placementsData?.studentPhone || '+91 8535072921'}</span>
                <span><i className="fas fa-clock text-crimson mr-2"></i> {placementsData?.studentTimings || 'Monday - Friday, 9:00 AM - 6:00 PM'}</span>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default PlacementsPage;